import { createContext, useContext, useEffect, useMemo, useState } from "react"
import { useAuth } from "@/contexts/AuthContext"
import { useUserPreferences, TEXT_SCALE_VALUES } from "@/contexts/UserPreferencesContext"
import { usePublicTenantBootstrap, useTenantSettings } from "@/hooks/use-tenant-settings"
import { applyThemeToDocument, resolveSystemColorMode } from "@/theme/applyTheme"
import { applyBrandOverride, defaultThemeId, getThemeDefinition, listAvailableThemes, migrateStoredThemeId } from "@/theme/registry"
import type { ActiveTheme, AvailableTheme, ColorMode, ResolvedColorMode, ThemeId } from "@/theme/types"

type ThemeProviderProps = {
  children: React.ReactNode
  defaultTheme?: ColorMode
  defaultThemeId?: ThemeId
}

type ThemeProviderState = {
  theme: ColorMode
  setTheme: (theme: ColorMode) => void
  colorMode: ColorMode
  resolvedColorMode: ResolvedColorMode
  themeId: ThemeId
  setThemeId: (themeId: ThemeId) => void
  themes: AvailableTheme[]
  activeTheme: ActiveTheme
}

const initialTheme = getThemeDefinition(defaultThemeId)

const initialState: ThemeProviderState = {
  theme: "system",
  setTheme: () => null,
  colorMode: "system",
  resolvedColorMode: "dark",
  themeId: defaultThemeId,
  setThemeId: () => null,
  themes: listAvailableThemes(),
  activeTheme: initialTheme,
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
  children,
  defaultTheme = "system",
  defaultThemeId: initialThemeId = defaultThemeId,
}: ThemeProviderProps) {
  const { user } = useAuth()
  const { preferences, setPreference } = useUserPreferences()
  const { data: tenantSettings } = useTenantSettings({ enabled: !!user })
  const { data: publicTenantBootstrap } = usePublicTenantBootstrap()
  const [resolvedColorMode, setResolvedColorMode] = useState<ResolvedColorMode>(resolveSystemColorMode())

  // One-time migration for deprecated theme IDs in localStorage
  useEffect(() => { migrateStoredThemeId() }, [])

  const effectiveUiTheme = tenantSettings?.ui_theme ?? publicTenantBootstrap?.ui_theme ?? null
  const allowUserOverride = effectiveUiTheme?.allow_user_override ?? true
  const colorMode = preferences.theme || defaultTheme
  const themeId = (allowUserOverride ? preferences.themeId : undefined) || effectiveUiTheme?.default_theme_id || initialThemeId

  useEffect(() => {
    if (typeof window === "undefined") return
    const media = window.matchMedia("(prefers-color-scheme: dark)")
    const sync = () => {
      setResolvedColorMode(colorMode === "system" ? (media.matches ? "dark" : "light") : colorMode)
    }
    sync()
    media.addEventListener("change", sync)
    return () => media.removeEventListener("change", sync)
  }, [colorMode])

  const activeTheme = useMemo(() => {
    const baseTheme = getThemeDefinition(themeId)
    return applyBrandOverride(baseTheme, effectiveUiTheme?.brand) as ActiveTheme
  }, [effectiveUiTheme?.brand, themeId])

  useEffect(() => {
    applyThemeToDocument(activeTheme, resolvedColorMode)
  }, [activeTheme, resolvedColorMode])

  useEffect(() => {
    localStorage.setItem("eca-ui-theme", colorMode)
    localStorage.setItem("eca-ui-theme-id", themeId)
  }, [colorMode, themeId])

  useEffect(() => {
    if (typeof document === "undefined") return
    const size = TEXT_SCALE_VALUES[preferences.textScale] || "16px"
    document.documentElement.style.fontSize = size
  }, [preferences.textScale])

  const value = useMemo<ThemeProviderState>(() => ({
    theme: colorMode,
    setTheme: (nextTheme: ColorMode) => {
      setPreference("theme", nextTheme)
    },
    colorMode,
    resolvedColorMode,
    themeId,
    setThemeId: (nextThemeId: ThemeId) => {
      if (!allowUserOverride) return
      setPreference("themeId", nextThemeId)
    },
    themes: listAvailableThemes(),
    activeTheme,
  }), [activeTheme, allowUserOverride, colorMode, resolvedColorMode, setPreference, themeId])

  return (
    <ThemeProviderContext.Provider value={value}>
      {children}
    </ThemeProviderContext.Provider>
  )
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext)
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider")
  }
  return context
}
